/* eslint-disable no-unsafe-optional-chaining */
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toGroupMigration } from '@data/dto/group-migration.dto';
import { Meta, PaginateResult } from '@data/models/api.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  GroupMigration,
  GroupMigrationHttp,
  GroupMigrationWizard,
} from '@data/models/group-migration.interface';
import { TrainingCourseHttp } from '@data/models/training-course.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class GroupMigrationService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly groupMigrationEndpoint = '/group-migration-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listGroupMigrations(
    filters: FilterParams,
  ): Promise<PaginateResult<GroupMigration[]>> {
    const { data: response, error } = await this.API?.get<{
      data: GroupMigrationHttp[];
      included?: TrainingCourseHttp[];
      meta: Meta;
    }>(this.groupMigrationEndpoint, {
      ...filters,
      include: ['training_courses'],
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items:
        response?.data?.map((data) =>
          toGroupMigration(data, response.included || []),
        ) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertGroupMigration({
    id,
    ...groupMigrationWizard
  }: GroupMigrationWizard): Promise<GroupMigration | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<GroupMigrationWizard, 'id'> },
        { data: GroupMigrationHttp; included?: TrainingCourseHttp[] }
      >(`${this.groupMigrationEndpoint}?include=training_courses`, {
        data: groupMigrationWizard,
      });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toGroupMigration(data?.data || {}, data?.included || []);
    }

    const { data, error } = await this.API?.patch<
      { data: GroupMigrationWizard },
      { data: GroupMigrationHttp; included?: TrainingCourseHttp[] }
    >(`${this.groupMigrationEndpoint}/${id}?include=training_courses`, {
      data: { ...groupMigrationWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }
    return toGroupMigration(data?.data || {}, data?.included || []);
  }

  public async deleteGroupMigration(
    id: string,
  ): Promise<GroupMigration | void> {
    if (!id) {
      throw new Error('Id is missing');
    }

    const { data, error } = await this.API?.delete<{
      data: GroupMigrationHttp;
    }>(`${this.groupMigrationEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toGroupMigration(data?.data || {}, []);
  }
}
